import {Component, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, effect, inject, OnInit} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {DsButtonModule, DsTabsModule} from '@bmw-ds/components';
import {TranslateModule} from '@ngx-translate/core';
import {IAuthInfo} from '@service-and-repairs/awpintegrationlib';
import {AuthInfoFactory} from '../../../auth/auth-info-factory';
import {AuthenticationTokenBearerService} from '../../../auth/authentication-token-bearer.service';
import {VehicleSearchOrigin} from '../../../core/analytics/enums/vehicle-search-origin';
import {KeyDataLoader} from '../../../core/keydata/services/key-data.loader';
import {ServiceCaseHolder} from '../../../core/service-case/models/service-case-holder';
import {ServiceCaseManager} from '../../../core/service-case/models/service-case-manager';
import {UserData} from '../../../core/user/models/user-data';
import {UserService} from '../../../core/user/services/user.service';
import {Util} from '../../../util/util';
import {CustomerDataFrontendService} from '../../customer-data/customer-data-frontend.service';
import {CustomerDataProfileService} from '../../customer-data/customer-data-profile.service';
import {CustomerDataSearchComponent} from '../../customer-data/customer-data-search.component';
import {CustomerSearchComponent} from '../../dms-search/customer-search/customer-search.component';
import {DmsLicensePlateSearchComponent} from '../../dms-search/dms-license-plate-search.component';
import {DmsOrderSearchComponent} from '../../dms-search/dms-order-search.component';
import {DmsSystem} from '../../dms-search/dms-system';
import {DmsVehicle} from '../../dms-search/dms-vehicle';
import {DmsService} from '../../dms-search/dms.service';
import {KeyPoolComponent} from '../../key-pool/key-pool.component';
import {AdvancedSearchTab} from './advanced-search-tab/advanced-search-tab';
import {AdvancedSearchTabComponent} from './advanced-search-tab/advanced-search-tab.component';
import {KEY_POOL_TAB_TITLE_RESOURCE, VehicleSearchService} from './vehicle-search.service';

@Component({
  selector: 'app-advanced-search',
  templateUrl: './advanced-search.component.html',
  styleUrls: ['./advanced-search.component.scss'],
  imports: [
    DsButtonModule,
    DsTabsModule,
    AdvancedSearchTabComponent,
    CustomerDataSearchComponent,
    KeyPoolComponent,
    CustomerSearchComponent,
    DmsLicensePlateSearchComponent,
    DmsOrderSearchComponent,
    TranslateModule
  ],
  host: {
    '[class.hidden]': '!vehicleSearchService.advancedSearchVisible()'
  },
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AdvancedSearchComponent implements OnInit {
  protected readonly vehicleSearchService: VehicleSearchService = inject(VehicleSearchService);

  private readonly serviceCaseManager: ServiceCaseManager = inject(ServiceCaseManager);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly serviceCaseHolder: ServiceCaseHolder = inject(ServiceCaseHolder);
  private readonly userService: UserService = inject(UserService);
  private readonly authenticationTokenBearerService: AuthenticationTokenBearerService
    = inject(AuthenticationTokenBearerService);
  private readonly dmsService: DmsService = inject(DmsService);
  private readonly keyDataLoader: KeyDataLoader = inject(KeyDataLoader);
  private readonly customerDataFrontendService: CustomerDataFrontendService = inject(CustomerDataFrontendService);
  private readonly customerDataProfileService: CustomerDataProfileService = inject(CustomerDataProfileService);

  tabs: AdvancedSearchTab[] = [];
  selectedTab: AdvancedSearchTab;

  user: UserData;
  authInfo: IAuthInfo;

  // used in html
  protected readonly VehicleSearchOrigin = VehicleSearchOrigin;

  constructor() {
    this.dmsService.dmsSettingsChanged
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((): void => this.createTabs());
    this.customerDataFrontendService.availabilityChanged
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((): void => this.createTabs());
    effect((): void => this.handleVisibilityChange());
  }

  ngOnInit(): void {
    this.userService.user$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(this.handleUserChange.bind(this));
    this.authenticationTokenBearerService.oidcTokenSubject
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(this.updateAuthInfo.bind(this));
    this.authenticationTokenBearerService.csslTokenSubject
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(this.updateAuthInfo.bind(this));
    this.createTabs();
    this.restoreSelectedTab();
  }

  hide(): void {
    this.vehicleSearchService.closeAdvancedSearch();
  }

  selectTab(tab: AdvancedSearchTab): void {
    this.selectedTab = tab;
    this.showSelectedTab();
  }

  typeCodeSelected(typeCode: any): void {
    this.hide();
    this.serviceCaseManager.openNewServiceCaseByTypeCode(typeCode, VehicleSearchOrigin.TYPE_ATTRIBUTES);
    this.vehicleSearchService.postVehicleSearchAnalyticsEvents(VehicleSearchOrigin.TYPE_ATTRIBUTES, typeCode);
  }

  vinSelected(vin: string): void {
    this.hide();
    this.serviceCaseManager.selectAndOpenServiceCaseByVin(vin, VehicleSearchOrigin.KEY_POOL);
    this.vehicleSearchService.postVehicleSearchAnalyticsEvents(VehicleSearchOrigin.KEY_POOL, vin);
  }

  dmsVehicleSelected(dmsVehicle: DmsVehicle, origin: string): void {
    this.hide();
    this.serviceCaseManager.selectAndOpenServiceCaseByDmsVehicle(dmsVehicle, origin);
    this.vehicleSearchService.postVehicleSearchAnalyticsEvents(origin, dmsVehicle?.vin);
  }

  customerSearchEnabled(): boolean {
    return this.dmsService.isCustomerSearchEnabled(DmsSystem.ANY);
  }

  orderSearchEnabled(): boolean {
    return this.dmsService.isOrderSearchEnabled(DmsSystem.ANY);
  }

  licensePlateSearchEnabled(): boolean {
    return this.dmsService.isLicensePlateSearchEnabled(DmsSystem.ANY);
  }

  customerDataSearchEnabled(): boolean {
    return this.customerDataFrontendService.isEnabled();
  }

  /**
   * Opens a service case for the vehicle the user selected in the Customer Data Frontend.
   */
  customerDataVehicleSelected(vin: string): void {
    this.hide();
    this.serviceCaseManager.selectAndOpenServiceCaseByVin(vin, VehicleSearchOrigin.CUSTOMER_DATA_FRONTEND);
    this.vehicleSearchService.postVehicleSearchAnalyticsEvents(VehicleSearchOrigin.CUSTOMER_DATA_FRONTEND, vin);
  }

  /**
   * Shows the profile of the customer the user selected in the Customer Data Frontend. The advanced search stays open
   * below the profile's overlay, so the profile offers a back button leading back to the search results.
   */
  customerDataProfileSelected(accountNumber: string): void {
    this.customerDataProfileService.open(accountNumber);
  }

  /**
   * Activates the requested tab and (re)loads its content whenever the advanced search becomes visible.
   */
  private handleVisibilityChange(): void {
    const requestedTabTitleResource: string = this.vehicleSearchService.requestedTabTitleResource();
    if (!this.vehicleSearchService.advancedSearchVisible()) {
      return;
    }
    const requestedTab: AdvancedSearchTab
      = this.tabs.find((tab: AdvancedSearchTab): boolean => tab.titleResource === requestedTabTitleResource);
    if (requestedTab) {
      this.selectedTab = requestedTab;
    }
    this.showSelectedTab();
  }

  private updateAuthInfo(): void {
    const userData = this.userService.getCurrentUser();
    if (userData) {
      this.authInfo = AuthInfoFactory.makeAuthInfo(userData, this.serviceCaseHolder.getActiveCase()?.getBrand());
    }
  }

  private handleUserChange(user: UserData): void {
    this.user = user;
    this.createTabs();
  }

  private createTabs(): void {
    this.tabs = [];
    this.tabs.push({
      titleResource: KEY_POOL_TAB_TITLE_RESOURCE
    });
    if (this.customerDataSearchEnabled()) {
      this.tabs.push({
        titleResource: 'case.customerAndLicenseSearch',
        src: this.customerDataFrontendService.getWebComponentScriptUrl()
      });
    }
    this.tabs.push({
      titleResource: 'case.typeAttributes',
      src: '/ui/typecode/main.mjs'
    });
    if (this.customerSearchEnabled()) {
      this.tabs.push({
        titleResource: 'case.customerName'
      });
    }
    if (this.licensePlateSearchEnabled()) {
      this.tabs.push({
        titleResource: 'case.licensePlate'
      });
    }
    if (this.orderSearchEnabled()) {
      this.tabs.push({
        titleResource: 'case.dmsOrderNumber'
      });
    }
    // Keep the selected tab in sync with the recreated tab instances.
    this.selectedTab = this.tabs
      .find((tab: AdvancedSearchTab): boolean => tab.titleResource === this.selectedTab?.titleResource)
      || this.selectedTab;
  }

  private showSelectedTab(): void {
    this.selectedTab.showErrorIndicator = false;
    if (this.selectedTab.src) {
      this.loadWebComponent(this.selectedTab);
    }
    this.vehicleSearchService.setSelectedTab(this.selectedTab.titleResource);
    if (this.selectedTab.titleResource === KEY_POOL_TAB_TITLE_RESOURCE) {
      this.keyDataLoader.reloadKeyData();
    }
  }

  private restoreSelectedTab(): void {
    this.selectedTab = this.tabs
      .find(tab => (tab.titleResource === this.vehicleSearchService.state.selectedTab)) || this.tabs[0];
  }

  private loadWebComponent(selectedTab: AdvancedSearchTab): void {
    selectedTab.showLoadingIndicator = true;
    Util.attachScript(selectedTab.src)
      .catch(() => selectedTab.showErrorIndicator = true)
      .finally(() => selectedTab.showLoadingIndicator = false);
  }
}
